import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";

/**
 * MiniSelect — compact but comfortable dropdown.
 *
 * Drop-in API (unchanged): { options:[{value,label,desc?}], value, onChange, label, icon }
 * Reimplemented on Radix Select so it now:
 *   • portals to <body> (never clipped by panels / overflow-hidden parents)
 *   • flips & repositions at viewport edges
 *   • opens to a generous height (up to 60vh) instead of a 176px scroll slit
 *   • shows label + description, keyboard nav + type-ahead, gold selected state
 */
export default function MiniSelect({
  options = [],
  value,
  onChange,
  label,
  icon: Icon,
  placeholder,
  className = "",
  disabled = false,
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        className={`group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left
          bg-[var(--bg-elevated)] border border-[var(--border-default)]
          text-[13px] text-[var(--text-primary)] shadow-[var(--shadow-xs)]
          transition-all duration-200
          hover:border-[rgba(212,168,83,0.4)] hover:bg-[var(--bg-overlay)]
          focus:outline-none focus-visible:border-[var(--color-gold-500)]
          focus-visible:shadow-[var(--shadow-glow-gold)]
          data-[state=open]:border-[var(--color-gold-500)] data-[state=open]:shadow-[var(--shadow-glow-gold)]
          data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed ${className}`}
      >
        {Icon && <Icon size={15} className="text-[var(--color-gold-400)] flex-shrink-0" />}
        <span className="flex-1 truncate">
          <SelectPrimitive.Value placeholder={placeholder || label || "Select…"} />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            size={14}
            className="text-[var(--text-muted)] flex-shrink-0 transition-transform duration-200
              group-data-[state=open]:rotate-180 group-data-[state=open]:text-[var(--color-gold-400)]"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="z-[200] min-w-[var(--radix-select-trigger-width)] max-w-[min(92vw,360px)]
            max-h-[min(60vh,var(--radix-select-content-available-height))] overflow-hidden
            rounded-xl border border-[var(--border-strong)] bg-[rgba(20,20,24,0.97)] backdrop-blur-xl
            shadow-[var(--shadow-2xl)] ring-1 ring-[rgba(212,168,83,0.12)]
            origin-[var(--radix-select-content-transform-origin)]
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
        >
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center h-6 text-[var(--text-muted)] bg-gradient-to-b from-[var(--bg-overlay)] to-transparent">
            <ChevronDown size={14} className="rotate-180" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1.5">
            {options.map((opt) => (
              <SelectPrimitive.Item
                key={opt.value}
                value={opt.value}
                className="relative flex flex-col gap-0.5 rounded-lg px-3 py-2 pr-9
                  cursor-pointer select-none outline-none text-[var(--text-secondary)]
                  data-[highlighted]:bg-[rgba(212,168,83,0.12)] data-[highlighted]:text-[var(--text-primary)]
                  data-[state=checked]:text-[var(--color-gold-300)] transition-colors"
              >
                <SelectPrimitive.ItemText>
                  <span className="text-[13px] font-medium leading-tight">{opt.label}</span>
                </SelectPrimitive.ItemText>
                {opt.desc && (
                  <span className="text-[11px] text-[var(--text-muted)] leading-snug">{opt.desc}</span>
                )}
                <SelectPrimitive.ItemIndicator className="absolute right-3 top-2.5 text-[var(--color-gold-400)]">
                  <Check size={15} />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center h-6 text-[var(--text-muted)] bg-gradient-to-t from-[var(--bg-overlay)] to-transparent">
            <ChevronDown size={14} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
